import { usePreferences } from "../context/PreferencesContext";
import {
  getAvaaioAuthCopy,
  localizeAuthResponse,
} from "../utils/avaaioAuthCopy";
import {
  normalizeAvaaioLanguage,
} from "../utils/avaaioLanguage";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "../styles/AvaaioStarRating.css";

const DEFAULT_API_BASE =
  import.meta.env.VITE_API_BASE || "";

const PENDING_RATING_KEY =
  "avaaio_pending_star_rating";

type RatingSummary = {
  average: number;
  rating_count: number;
  distribution?: Record<string, number>;
};

type LaunchInterest = {
  interested_count: number;
  launch_date: string;
  launch_status: "prelaunch" | "launched";
  waitlist_open: boolean;
};

type SubmitOutcome =
  | "none"
  | "submitted"
  | "signin_required"
  | "failed";

type SubmitResult = {
  outcome: SubmitOutcome;
  summary?: RatingSummary;
};

const ratingLabels: Record<number, string> = {
  1: "Needs improvement",
  2: "Fair",
  3: "Good",
  4: "Excellent",
  5: "Incredible, truly amazing",
};

const normalizedApiBase = (
  apiBase: string
): string => {
  return String(apiBase || "").replace(/\/$/, "");
};

// __AVAAIO_LOGIN_RATING_SUBMIT_LAUNCH_COUNT_PHASE1__
const AVAAIO_LOGIN_RATING_BUILD =
  "verified-waitlist-phase2a-candidate";

export async function submitPendingAvaaioRating(
  apiBase = DEFAULT_API_BASE,
  token = ""
): Promise<SubmitResult> {
  let pendingRating = 0;

  try {
    pendingRating = Number(
      sessionStorage.getItem(
        PENDING_RATING_KEY
      ) || "0"
    );
  } catch {
    return {
      outcome: "none",
    };
  }

  if (
    !Number.isInteger(pendingRating) ||
    pendingRating < 1 ||
    pendingRating > 5
  ) {
    return {
      outcome: "none",
    };
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    4500
  );

  try {
    const response = await fetch(
      `${normalizedApiBase(apiBase)}/api/v1/auth/ratings`,
      {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token
            ? {
                Authorization:
                  `Bearer ${token}`,
              }
            : {}),
        },
        body: JSON.stringify({
          rating: pendingRating,
        }),
      }
    );

    if (
      response.status === 401 ||
      response.status === 403
    ) {
      return {
        outcome: "signin_required",
      };
    }

    if (!response.ok) {
      return {
        outcome: "failed",
      };
    }

    const data = await response.json();

    try {
      sessionStorage.removeItem(
        PENDING_RATING_KEY
      );
    } catch {}

    return {
      outcome: "submitted",
      summary: data?.summary,
    };
  } catch {
    return {
      outcome: "failed",
    };
  } finally {
    window.clearTimeout(timeout);
  }
}

const AvaaioStarRating: React.FC = () => {
  const [summary, setSummary] =
    useState<RatingSummary | null>(null);

  const [selectedRating, setSelectedRating] =
    useState(0);

  const { preferences } = usePreferences();
  const copy = getAvaaioAuthCopy(
    preferences.language
  );
  const ratingCopy = copy.rating;

  const [hoveredRating, setHoveredRating] =
    useState(0);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const [
    launchInterest,
    setLaunchInterest,
  ] = useState<LaunchInterest | null>(null);

  const [waitlistEmail, setWaitlistEmail] =
    useState("");

  const [
    waitlistLanguage,
    setWaitlistLanguage,
  ] = useState<string>(() =>
    normalizeAvaaioLanguage(
      preferences.language
    )
  );
  const [waitlistConsent, setWaitlistConsent] =
    useState(false);

  useEffect(() => {
    setWaitlistLanguage(
      normalizeAvaaioLanguage(
        preferences.language
      )
    );
  }, [preferences.language]);

  const [waitlistBusy, setWaitlistBusy] =
    useState(false);

  const [waitlistMessage, setWaitlistMessage] =
    useState("");

  const activeRating =
    hoveredRating || selectedRating;

  const activeLabel = useMemo(() => {
    return activeRating
      ? ratingCopy.labels[activeRating]
      : ratingCopy.select;
  }, [activeRating, ratingCopy]);

  const loadSummary = async () => {
    try {
      const response = await fetch(
        `${normalizedApiBase(
          DEFAULT_API_BASE
        )}/api/v1/auth/ratings/summary`,
        {
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) return;

      const data = await response.json();

      setSummary({
        average: Number(
          data?.average || 0
        ),
        rating_count: Number(
          data?.rating_count || 0
        ),
        distribution:
          data?.distribution || {},
      });
    } catch {
      // The rating control remains available even
      // if the public summary cannot be loaded.
    }
  };

  const loadLaunchInterest = async () => {
    try {
      const response = await fetch(
        `${normalizedApiBase(
          DEFAULT_API_BASE
        )}/api/v1/auth/launch-interest`,
        {
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) return;

      const data = await response.json();

      const interestedCount = Number(
        data?.interested_count || 0
      );

      const launchStatus =
        String(
          data?.launch_status || "prelaunch"
        ).toLowerCase() === "launched"
          ? "launched"
          : "prelaunch";

      const waitlistOpen =
        typeof data?.waitlist_open === "boolean"
          ? data.waitlist_open
          : launchStatus === "prelaunch";

      setLaunchInterest({
        interested_count:
          Number.isFinite(interestedCount) &&
          interestedCount >= 0
            ? Math.floor(interestedCount)
            : 0,
        launch_date:
          String(
            data?.launch_date ||
              "2026-12-13"
          ),
        launch_status: launchStatus,
        waitlist_open: waitlistOpen,
      });
    } catch {
      // The login and rating controls remain usable
      // if the aggregate launch count is unavailable.
    }
  };

  useEffect(() => {
    void loadSummary();
    void loadLaunchInterest();

    try {
      const pending = Number(
        sessionStorage.getItem(
          PENDING_RATING_KEY
        ) || "0"
      );

      if (
        Number.isInteger(pending) &&
        pending >= 1 &&
        pending <= 5
      ) {
        setSelectedRating(pending);
        setMessage(
          ratingCopy.signInSaved
        );
      }
    } catch {}
  }, []);

  const chooseRating = (
    rating: number
  ) => {
    if (busy) return;

    setSelectedRating(rating);

    try {
      sessionStorage.setItem(
        PENDING_RATING_KEY,
        String(rating)
      );
    } catch {}

    setMessage(
      ratingCopy.selected
    );
  };

  const submitSelectedRating = async () => {
    if (
      busy ||
      !Number.isInteger(selectedRating) ||
      selectedRating < 1 ||
      selectedRating > 5
    ) {
      return;
    }

    try {
      sessionStorage.setItem(
        PENDING_RATING_KEY,
        String(selectedRating)
      );
    } catch {}

    setBusy(true);
    setMessage("");

    try {
      const result =
        await submitPendingAvaaioRating();

      if (result.outcome === "submitted") {
        setMessage(
          ratingCopy.thankYou
        );

        if (result.summary) {
          setSummary(result.summary);
        } else {
          await loadSummary();
        }
      } else if (
        result.outcome === "signin_required"
      ) {
        setMessage(
          ratingCopy.signInRegister
        );
      } else if (
        result.outcome === "failed"
      ) {
        setMessage(
          ratingCopy.savedAfterSignIn
        );
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    try {
      const status =
        new URLSearchParams(
          window.location.search
        ).get("waitlist");

      if (status === "verified") {
        setWaitlistMessage(
          ratingCopy.verifiedWaitlist
        );
      } else if (status === "invalid") {
        setWaitlistMessage(
          ratingCopy.invalidWaitlist
        );
      }
    } catch {
      // Query-string feedback is optional.
    }
  }, []);

  const submitWaitlist = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const email = waitlistEmail
      .trim()
      .toLowerCase();

    if (!email) {
      setWaitlistMessage(
        ratingCopy.enterEmail
      );
      return;
    }

    if (!waitlistConsent) {
      setWaitlistMessage(
        ratingCopy.consentRequired
      );
      return;
    }

    setWaitlistBusy(true);
    setWaitlistMessage("");

    try {
      const response = await fetch(
        `${normalizedApiBase(
          DEFAULT_API_BASE
        )}/api/v1/auth/waitlist/join`,
        {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email,
            consent: true,
            preferred_language:
              waitlistLanguage,
          }),
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (response.status === 409) {
        setLaunchInterest((current) => ({
          interested_count:
            current?.interested_count || 0,
          launch_date:
            current?.launch_date ||
            "2026-12-13",
          launch_status: "launched",
          waitlist_open: false,
        }));

        setWaitlistMessage(
          ratingCopy.closed
        );
        return;
      }

      if (!response.ok) {
        const detail =
          typeof data?.detail === "string"
            ? data.detail
            : ratingCopy.requestFailed;

        setWaitlistMessage(
        localizeAuthResponse(
          detail,
          ratingCopy.requestFailed,
          copy
        )
      );
        return;
      }

      setWaitlistMessage(
        localizeAuthResponse(
          data?.message,
          ratingCopy.verifyEmail,
          copy
        )
      );

      setWaitlistEmail("");
      setWaitlistConsent(false);

      void loadLaunchInterest();
    } catch {
      setWaitlistMessage(
        ratingCopy.unavailable
      );
    } finally {
      setWaitlistBusy(false);
    }
  };

  const waitlistOpen =
    launchInterest?.waitlist_open ?? true;

  const summaryText =
    summary && summary.rating_count > 0
      ? `${summary.average.toLocaleString(
          copy.locale,
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }
        )} ${ratingCopy.average} · ${
          summary.rating_count.toLocaleString(
            copy.locale
          )
        } ${
          summary.rating_count === 1
            ? ratingCopy.rating
            : ratingCopy.ratings
        }`
      : ratingCopy.noRatings;

  const launchInterestText =
    !launchInterest
      ? ratingCopy.loadingInterest
      : `${
          launchInterest.interested_count
            .toLocaleString(copy.locale)
        } ${
          launchInterest.interested_count === 1
            ? ratingCopy.personInterested
            : ratingCopy.peopleInterested
        }`;

  const launchDateText = (() => {
    const rawDate =
      launchInterest?.launch_date ||
      "2026-12-13";

    const date = new Date(
      `${rawDate}T00:00:00Z`
    );

    const formatted = Number.isNaN(
      date.getTime()
    )
      ? rawDate
      : new Intl.DateTimeFormat(
          copy.locale,
          {
            dateStyle: "long",
            timeZone: "UTC",
          }
        ).format(date);

    return `${ratingCopy.launchingPrefix} ${formatted}`;
  })();

  return (
    <aside
      className="avaaio-rating-card"
        data-avaaio-build={
          AVAAIO_LOGIN_RATING_BUILD
        }
      aria-label={ratingCopy.title}
    >
      <div className="avaaio-rating-kicker">
        {ratingCopy.community}
      </div>

      <h2 className="avaaio-rating-title">
        {ratingCopy.title}
      </h2>

      <div
        className="avaaio-rating-stars"
        role="group"
        aria-label={ratingCopy.chooseRating}
        onMouseLeave={() =>
          setHoveredRating(0)
        }
      >
        {[1, 2, 3, 4, 5].map(
          (rating) => {
            const filled =
              rating <= activeRating;

            return (
              <button
                key={rating}
                type="button"
                className={`avaaio-rating-star ${
                  filled
                    ? "is-filled"
                    : ""
                }`}
                aria-label={`${rating} ${
                  rating === 1
                    ? ratingCopy.star
                    : ratingCopy.stars
                }: ${ratingCopy.labels[rating]}`}
                aria-pressed={
                  selectedRating === rating
                }
                disabled={busy}
                onMouseEnter={() =>
                  setHoveredRating(rating)
                }
                onFocus={() =>
                  setHoveredRating(rating)
                }
                onBlur={() =>
                  setHoveredRating(0)
                }
                onClick={() =>
                  void chooseRating(rating)
                }
              >
                {filled ? "★" : "☆"}
              </button>
            );
          }
        )}
      </div>        <div
          className="avaaio-rating-label"
          aria-live="polite"
        >
          {activeLabel}
        </div>

        <button
          type="button"
          className="avaaio-rating-submit"
          disabled={
            busy ||
            selectedRating < 1 ||
            selectedRating > 5
          }
          onClick={() =>
            void submitSelectedRating()
          }
        >
          {busy
            ? ratingCopy.submitting
            : ratingCopy.submit}
        </button>

        {waitlistOpen && (
          <div
            className="avaaio-launch-interest"
            aria-live="polite"
          >
            <div className="avaaio-launch-count">
              {launchInterestText}
            </div>

            <div className="avaaio-launch-date">
              {launchDateText}
            </div>

            <form
              className="avaaio-waitlist-form"
              onSubmit={submitWaitlist}
            >
              <label
                className="avaaio-waitlist-label"
                htmlFor="avaaio-waitlist-email"
              >
                {ratingCopy.joinLabel}
              </label>

              <input
                id="avaaio-waitlist-email"
                className="avaaio-waitlist-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength={254}
                value={waitlistEmail}
                placeholder={ratingCopy.emailAddress}
                disabled={waitlistBusy}
                onChange={(event) =>
                  setWaitlistEmail(
                    event.target.value
                  )
                }
              />

              <label
                className="avaaio-waitlist-language-label"
                htmlFor="avaaio-waitlist-language"
              >
                {ratingCopy.preferredEmailLanguage}
              </label>

              <select
                id="avaaio-waitlist-language"
                className="avaaio-waitlist-language"
                value={waitlistLanguage}
                disabled={waitlistBusy}
                onChange={(event) =>
                  setWaitlistLanguage(
                    event.target.value
                  )
                }
              >
                <option value="en">
                  English
                </option>

                <option value="es">
                  Español
                </option>

                <option value="fr">
                  Français
                </option>

                <option value="de">
                  Deutsch
                </option>

                <option value="it">
                  Italiano
                </option>

                <option value="pt">
                  Português
                </option>
              </select>

              <div
                className="avaaio-waitlist-language-note"
              >
                {ratingCopy.bilingualNote}
              </div>


              <label className="avaaio-waitlist-consent">
                <input
                  type="checkbox"
                  checked={waitlistConsent}
                  disabled={waitlistBusy}
                  onChange={(event) =>
                    setWaitlistConsent(
                      event.target.checked
                    )
                  }
                />

                <span>
                  {ratingCopy.consent}
                </span>
              </label>

              <button
                type="submit"
                className="avaaio-waitlist-submit"
                disabled={
                  waitlistBusy ||
                  !waitlistEmail.trim() ||
                  !waitlistConsent
                }
              >
                {waitlistBusy
                  ? ratingCopy.joining
                  : ratingCopy.join}
              </button>
            </form>

            {waitlistMessage && (
              <div
                className="avaaio-waitlist-message"
                role="status"
              >
                {waitlistMessage}
              </div>
            )}
          </div>
        )}

        <div className="avaaio-rating-summary">
          {summary && summary.rating_count > 0 && (
            <div
              className="avaaio-rating-overall-stars"
              role="img"
              aria-label={summaryText}
            >
              {[1, 2, 3, 4, 5].map((star) => {
                const fill = Math.max(
                  0,
                  Math.min(
                    1,
                    summary.average - (star - 1)
                  )
                );

                return (
                  <span
                    key={star}
                    className="avaaio-rating-overall-star"
                    aria-hidden="true"
                  >
                    <span className="avaaio-rating-overall-star-empty">
                      ★
                    </span>

                    <span
                      className="avaaio-rating-overall-star-fill"
                      style={{
                        width: `${fill * 100}%`,
                      }}
                    >
                      ★
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          <div className="avaaio-rating-summary-text">
            {summaryText}
          </div>
        </div>
{message && (
        <div
          className="avaaio-rating-message"
          aria-live="polite"
        >
          {message}
        </div>
      )}

      <div className="avaaio-rating-privacy">
        No comments. One private rating per
        verified account.
      </div>
    </aside>
  );
};

export default AvaaioStarRating;
